import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Trash2, Send, FileQuestion, Info } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'react-toastify';

import { Card, Button, Badge, AreaChip, Textarea, Skeleton } from '../ui-v2';
import { UsuarioContext } from '../../providers/personaContext';
import useImagenBlob from '../../../funciones/useImagenBlob';
import { getTicket, crearComentario, eliminarAdjunto } from './api';
import EstadoProgress from './components/EstadoProgress';

const ESTADO_META = {
  pendiente: { label: 'Pendiente', variant: 'warning' },
  en_revision: { label: 'En revisión', variant: 'default' },
  en_desarrollo: { label: 'En desarrollo', variant: 'default' },
  resuelto: { label: 'Resuelto', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'destructive' },
  descartado: { label: 'Descartado', variant: 'secondary' },
  recibida: { label: 'Recibida', variant: 'secondary' },
  en_estudio: { label: 'En estudio', variant: 'default' },
  aceptada: { label: 'Aceptada', variant: 'success' },
  implementada: { label: 'Implementada', variant: 'success' },
  rechazada: { label: 'Rechazada', variant: 'destructive' },
};
const PRIORIDAD_LABEL = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };
const ESTADOS_TERMINALES = ['resuelto', 'rechazado', 'descartado', 'implementada', 'rechazada'];

function fecha(f) {
  if (!f) return null;
  try { return format(new Date(f), "d MMM yyyy, HH:mm", { locale: es }); } catch { return null; }
}
function hace(f) {
  try { return formatDistanceToNow(new Date(f), { addSuffix: true, locale: es }); } catch { return ''; }
}

export default function SoporteTicketDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { persona } = useContext(UsuarioContext);
  const [ticket, setTicket] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [noEncontrado, setNoEncontrado] = useState(false);

  const [nuevoComentario, setNuevoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [borrandoId, setBorrandoId] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true); setNoEncontrado(false);
    return getTicket(id)
      .then((t) => setTicket(t))
      .catch(() => setNoEncontrado(true))
      .finally(() => setCargando(false));
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const terminal = ticket && ESTADOS_TERMINALES.includes(ticket.estado);

  async function enviarComentario() {
    const contenido = nuevoComentario.trim();
    if (!contenido) return;
    setEnviandoComentario(true);
    try {
      await crearComentario(ticket.id, contenido);
      setNuevoComentario('');
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo añadir el comentario.');
    } finally {
      setEnviandoComentario(false);
    }
  }

  async function borrarAdjunto(adjuntoId) {
    setBorrandoId(adjuntoId);
    try {
      await eliminarAdjunto(ticket.id, adjuntoId);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo eliminar la imagen.');
    } finally {
      setBorrandoId(null);
    }
  }

  if (cargando) return <SkeletonTicketDetalle />;
  if (noEncontrado || !ticket) {
    return (
      <div className="px-6 py-16 text-center">
        <FileQuestion className="w-12 h-12 mx-auto text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-700">Ticket no encontrado</p>
        <p className="text-sm text-slate-500 mt-1">El ticket no existe o no tienes acceso a él.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/soporte/mis-tickets')}>
          <ArrowLeft className="w-4 h-4" /> Volver a mis tickets
        </Button>
      </div>
    );
  }

  const est = ESTADO_META[ticket.estado] || { label: ticket.estado, variant: 'secondary' };
  const adjuntos = ticket.adjuntos || [];
  const comentarios = ticket.comentarios || [];

  return (
    <div className="px-6 py-6 min-h-full">
      <button
        type="button"
        onClick={() => navigate('/soporte/mis-tickets')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Mis tickets / #{ticket.numero}
      </button>

      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <code className="text-[11px] font-mono text-slate-400">#{ticket.numero}</code>
            <Badge variant="outline" className="text-[10px]">
              {ticket.tipo === 'incidencia' ? 'Incidencia' : 'Sugerencia'}
            </Badge>
          </div>
          <h1 className="text-xl font-semibold text-slate-900">{ticket.titulo}</h1>
        </div>
        <Badge variant={est.variant} className="shrink-0 mt-1">{est.label}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 items-start">
        {/* Principal */}
        <div className="space-y-6">
          {/* Progreso del estado */}
          <Card className="p-5 border-slate-200">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Estado</div>
            <EstadoProgress tipo={ticket.tipo} estado={ticket.estado} />
          </Card>

          <Card className="p-6 border-slate-200">
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {ticket.categoria && <AreaChip area={ticket.categoria.color || 'otros'}>{ticket.categoria.nombre}</AreaChip>}
              {ticket.modulo_intranet && <span className="text-xs text-slate-500">Módulo: {ticket.modulo_intranet}</span>}
              {ticket.tipo_dispositivo && <span className="text-xs text-slate-500">Dispositivo: {ticket.tipo_dispositivo}</span>}
              {ticket.tipo === 'incidencia' && ticket.prioridad && (
                <span className="text-xs text-slate-500">Prioridad: {PRIORIDAD_LABEL[ticket.prioridad] || ticket.prioridad}</span>
              )}
            </div>

            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Descripción</div>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{ticket.descripcion}</p>

            {/* Adjuntos */}
            {adjuntos.length > 0 && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                  Imágenes adjuntas ({adjuntos.length})
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {adjuntos.map((a) => (
                    <AdjuntoThumb key={a.id} adjunto={a} puedeBorrar={!terminal} onDelete={borrarAdjunto} borrando={borrandoId === a.id} />
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Comentarios */}
          <Card className="p-6 border-slate-200">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Comentarios</div>

            {comentarios.length === 0 ? (
              <p className="text-sm text-slate-400 italic">Aún no hay comentarios.</p>
            ) : (
              <ul className="space-y-4">
                {comentarios.map((c) => {
                  const esMio = persona && c.persona_id === persona.id;
                  const autor = esMio ? 'Tú' : 'Soporte';
                  return (
                    <li key={c.id} className="flex gap-3">
                      <div className={'w-8 h-8 rounded-full shrink-0 grid place-items-center text-[11px] font-semibold ' +
                        (esMio ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500')}>
                        {esMio ? 'Tú' : 'S'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900">{autor}</span>
                          <span className="text-[11px] text-slate-400">{hace(c.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{c.contenido}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Form de comentario o aviso de cerrado */}
            <div className="mt-5 pt-5 border-t border-slate-100">
              {terminal ? (
                <p className="text-sm text-slate-400 italic">Este ticket está cerrado y no admite nuevos comentarios.</p>
              ) : (
                <div>
                  <Textarea
                    value={nuevoComentario}
                    onChange={(e) => setNuevoComentario(e.target.value)}
                    rows={3}
                    maxLength={5000}
                    placeholder="Escribe un comentario…"
                  />
                  <div className="flex justify-end mt-2">
                    <Button onClick={enviarComentario} disabled={enviandoComentario || !nuevoComentario.trim()}>
                      {enviandoComentario ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Añadir comentario
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Aside metadatos */}
        <Card className="p-5 border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Detalles</div>
          <ul className="space-y-2.5 text-sm">
            <Meta k="Número" v={<code className="font-mono text-xs">#{ticket.numero}</code>} />
            <Meta k="Estado" v={<Badge variant={est.variant}>{est.label}</Badge>} />
            {ticket.tipo === 'incidencia' && (
              <Meta k="Prioridad" v={ticket.prioridad ? (PRIORIDAD_LABEL[ticket.prioridad] || ticket.prioridad) : '—'} />
            )}
            {ticket.categoria && <Meta k="Área" v={ticket.categoria.nombre} />}
            {ticket.modulo_intranet && <Meta k="Módulo" v={ticket.modulo_intranet} />}
            {ticket.tipo_dispositivo && <Meta k="Dispositivo" v={ticket.tipo_dispositivo} />}
            <Meta k="Creado" v={fecha(ticket.createdAt) || '—'} />
            <Meta k="Actualizado" v={fecha(ticket.updatedAt) || '—'} />
            {ticket.resuelto_at && <Meta k="Resuelto" v={fecha(ticket.resuelto_at)} />}
          </ul>
          {/* 3.5.G — señal para el usuario si el ticket lo registró soporte por él
              (sin exponer el nombre del técnico: es info interna). */}
          {ticket.creado_por && (
            <div className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Este ticket fue registrado por el equipo de soporte a partir de tu contacto.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function AdjuntoThumb({ adjunto, puedeBorrar, onDelete, borrando }) {
  const src = useImagenBlob(`${process.env.REACT_APP_API_URL}${adjunto.download_url}`);
  return (
    <div className="relative group">
      <a
        href={src || undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="block aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-50"
        title={adjunto.nombre_original}
      >
        {src
          ? <img src={src} alt={adjunto.nombre_original} className="w-full h-full object-cover" />
          : <div className="w-full h-full grid place-items-center text-slate-300"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      </a>
      {puedeBorrar && (
        <button
          type="button"
          onClick={() => onDelete(adjunto.id)}
          disabled={borrando}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-800/80 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-100 disabled:bg-slate-800/80"
          title="Eliminar imagen"
        >
          {borrando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-slate-500 shrink-0">{k}</span>
      <span className="text-slate-900 text-right">{v}</span>
    </li>
  );
}

// Skeleton del detalle de usuario: replica el grid real (principal + aside 280px)
// para que no haya layout shift. Genérico, no pixel-perfect.
function SkeletonTicketDetalle() {
  return (
    <div className="px-6 py-6 min-h-full">
      <Skeleton className="h-4 w-44 mb-4" />
      <div className="flex items-start justify-between gap-3 mb-6">
        <div><Skeleton className="h-3 w-28 mb-2" /><Skeleton className="h-6 w-80" /></div>
        <Skeleton className="h-5 w-20 rounded-full mt-1" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 items-start">
        {/* Principal */}
        <div className="space-y-6">
          <Card className="p-5 border-slate-200">
            <Skeleton className="h-3 w-16 mb-4" />
            <Skeleton className="h-2 w-full rounded-full" />
          </Card>
          <Card className="p-6 border-slate-200">
            <div className="flex gap-2 mb-4"><Skeleton className="h-5 w-24 rounded-full" /><Skeleton className="h-4 w-24" /></div>
            <Skeleton className="h-3 w-24 mb-2" />
            <div className="space-y-2"><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-2/3" /></div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="aspect-square rounded-md" />)}</div>
          </Card>
          <Card className="p-6 border-slate-200">
            <Skeleton className="h-3 w-28 mb-4" />
            <div className="space-y-4">
              {[0, 1].map((i) => (
                <div key={i} className="flex gap-3"><Skeleton className="w-8 h-8 rounded-full shrink-0" /><div className="flex-1"><Skeleton className="h-3 w-32 mb-2" /><Skeleton className="h-3 w-full" /></div></div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <Skeleton className="h-16 w-full rounded-md mb-2" />
              <div className="flex justify-end"><Skeleton className="h-9 w-40 rounded-md" /></div>
            </div>
          </Card>
        </div>
        {/* Aside metadatos */}
        <Card className="p-5 border-slate-200">
          <Skeleton className="h-3 w-20 mb-3" />
          <div className="space-y-2.5">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="flex justify-between"><Skeleton className="h-3 w-16" /><Skeleton className="h-3 w-20" /></div>)}</div>
        </Card>
      </div>
    </div>
  );
}
